import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { fetchSiteReceiver } from '../contacts/site-receiver';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { shipmentsApi } from '../../api/shipments-api';
import { useAuth } from '../../auth/AuthContext';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect, type SearchableSelectOption } from '../../components/SearchableSelect';
import type { Customer, Site } from '../../domain/inventory';
import {
  returnIsCollected,
  CARGONIZER_RETURN_CARRIERS,
  packageMissingMeasurements,
  type ShipmentCreateReturnInput,
  type ShipmentDirection,
  shipsBoxes,
} from '../../domain/shipment';
import {
  formatSiteAddress,
  hasSiteAddress,
  isValidPhone,
  PHONE_HINT,
  tidyPhone,
} from '../../domain/siteContact';
import { siteDisplayName, siteLabel, siteSearchAliases } from '../../lib/site-label';
import { bookingErrorMessage, isUnknownOutcome } from './booking-errors';
import { DirectionChoice } from './DirectionChoice';
import { RebillFreightField } from './RebillFreightField';
import { rebillOptions, rebillReferenceProblem } from './rebill-freight';
import { PalletEditor } from './PalletEditor';
import { EMPTY_PALLET, palletMeasurements, type PalletDraft } from './pallet-draft';

interface ReturnShipmentFormProps {
  onClose: () => void;
  onCreated: (shipmentId: string) => void;
  onDirectionChange: (direction: ShipmentDirection) => void;
  initialOriginSiteId?: string | undefined;
  initialReason?: string | undefined;
}

type ReturnCarrier = (typeof CARGONIZER_RETURN_CARRIERS)[number];

interface OriginDraft {
  name: string;
  contactName: string;
  contactPhone: string;
  line1: string;
  line2: string;
  postcode: string;
  city: string;
}

const EMPTY_ORIGIN: OriginDraft = {
  name: '',
  contactName: '',
  contactPhone: '',
  line1: '',
  line2: '',
  postcode: '',
  city: '',
};

function customerLabel(customer: Customer): string {
  return customer.displayName?.trim() || customer.name;
}

// The pickup party name in the "<Customer> <Site>" form Cargonizer's
// address book uses, e.g. "Mowi Kråknes".
function originName(site: Site, customer: Customer | undefined): string {
  const name = siteDisplayName(site);
  return customer ? `${customerLabel(customer)} ${name}` : name;
}

/**
 * Books a customer return with a Cargonizer return product: collected from
 * a customer Site, delivered to a Tidal location. The pickup details are
 * prefilled from the Site and can be corrected for this shipment only --
 * the Site itself is never changed from here.
 */
export function ReturnShipmentForm({
  onClose,
  onCreated,
  onDirectionChange,
  initialOriginSiteId,
  initialReason,
}: ReturnShipmentFormProps) {
  const queryClient = useQueryClient();
  const { canMutate } = useAuth();
  const formId = useId();
  const [siteId, setSiteId] = useState('');
  const [origin, setOrigin] = useState<OriginDraft>(EMPTY_ORIGIN);
  const [destinationSiteId, setDestinationSiteId] = useState('');
  const [carrier, setCarrier] = useState<ReturnCarrier>(CARGONIZER_RETURN_CARRIERS[0]);
  const collected = returnIsCollected(carrier);
  const [reason, setReason] = useState(initialReason ?? '');
  const [senderReference, setSenderReference] = useState('');
  const [pallets, setPallets] = useState<PalletDraft[]>([{ ...EMPTY_PALLET }]);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const destinations = useQuery({
    queryKey: ['shipment-return-destinations'],
    queryFn: () => shipmentsApi.listReturnDestinations(),
    staleTime: 5 * 60 * 1000,
  });

  const tidalSiteIds = useMemo(
    () => new Set((destinations.data ?? []).map((d) => d.siteId).filter(Boolean)),
    [destinations.data],
  );
  const customersById = useMemo(
    () => new Map((customers.data ?? []).map((customer) => [customer.id, customer])),
    [customers.data],
  );
  // Every customer site, searchable by site or customer: choosing the site
  // also settles the customer. Tidal locations are destinations, never
  // origins.
  const siteOptions = useMemo<SearchableSelectOption[]>(
    () =>
      (sites.data ?? [])
        .filter((site) => !tidalSiteIds.has(site.id))
        .map((site) => {
          const customer = customersById.get(site.customerId);
          const label = customer
            ? `${siteLabel(site)} — ${customerLabel(customer)}`
            : siteLabel(site);
          return {
            value: site.id,
            label: hasSiteAddress(site.address) ? label : `${label} (no address)`,
            aliases: [
              ...siteSearchAliases(site),
              formatSiteAddress(site.address) ?? '',
              customer?.name ?? '',
            ].filter(Boolean),
          };
        })
        .sort((left, right) => left.label.localeCompare(right.label)),
    [customersById, sites.data, tidalSiteIds],
  );
  const selectedSite = sites.data?.find((site) => site.id === siteId);
  const selectedCustomer = selectedSite ? customersById.get(selectedSite.customerId) : undefined;
  const destination = destinations.data?.find((d) => d.siteId === destinationSiteId);

  // Default to the first approved Tidal location.
  useEffect(() => {
    if (!destinationSiteId && destinations.data?.[0]?.siteId) {
      setDestinationSiteId(destinations.data[0].siteId);
    }
  }, [destinationSiteId, destinations.data]);

  function applySite(nextSiteId: string): void {
    setSiteId(nextSiteId);
    const site = sites.data?.find((candidate) => candidate.id === nextSiteId);
    if (!site) {
      setOrigin(EMPTY_ORIGIN);
      return;
    }
    setOrigin({
      name: originName(site, customersById.get(site.customerId)),
      contactName: '',
      contactPhone: '',
      line1: site.address?.line1 ?? '',
      line2: site.address?.line2 ?? '',
      postcode: site.address?.postcode ?? '',
      city: site.address?.city ?? '',
    });
    // Who hands the goods over: the site's Receiving (else Operations)
    // contact (tidaltech/koi#8359), unless someone typed one meanwhile.
    void fetchSiteReceiver(site.id).then((receiver) => {
      if (!receiver) return;
      setOrigin((current) =>
        current.contactName || current.contactPhone
          ? current
          : { ...current, contactName: receiver.name, contactPhone: receiver.phone ?? '' },
      );
    });
  }

  const appliedInitialSite = useRef(false);
  useEffect(() => {
    if (appliedInitialSite.current || !initialOriginSiteId || !sites.data || !customers.data)
      return;
    appliedInitialSite.current = true;
    const site = sites.data.find((candidate) => candidate.id === initialOriginSiteId);
    if (!site) return;
    applySite(site.id);
    // applySite only reads the query data checked above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOriginSiteId, sites.data, customers.data]);

  const mutation = useMutation({
    mutationFn: (input: ShipmentCreateReturnInput) =>
      shipmentsApi.createReturnShipmentViaCargonizer(input),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['shipments'] });
      onCreated(created.id);
    },
    onError: (error: unknown) => {
      if (isUnknownOutcome(error)) {
        setUnknownOutcome(true);
        return;
      }
      setFormError(bookingErrorMessage(error));
    },
  });

  function updateOrigin(field: keyof OriginDraft, value: string): void {
    setOrigin((current) => ({ ...current, [field]: value }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    setUnknownOutcome(false);
    if (!siteId) {
      setFormError('Choose the customer site the return is collected from.');
      return;
    }
    if (!destinationSiteId) {
      setFormError('Choose the Tidal location the return goes to.');
      return;
    }
    const address = {
      line1: origin.line1.trim(),
      postcode: origin.postcode.trim(),
      city: origin.city.trim(),
      ...(origin.line2.trim() ? { line2: origin.line2.trim() } : {}),
    };
    if (!origin.name.trim() || !address.line1 || !address.postcode || !address.city) {
      setFormError('The pickup name, street, postcode and city are required.');
      return;
    }
    // Identical packages become one booked package each.
    const packages = pallets.flatMap((p) =>
      Array.from({ length: Math.max(1, Math.floor(Number(p.copies) || 1)) }, () =>
        palletMeasurements(p),
      ),
    );
    if (pallets.some((p) => p.copies && !(Number(p.copies) >= 1))) {
      setFormError('Identical packages must be 1 or more.');
      return;
    }
    const rebillProblem = rebillReferenceProblem(senderReference);
    if (rebillProblem) {
      setFormError(rebillProblem);
      return;
    }
    if (!isValidPhone(origin.contactPhone)) {
      setFormError(`The phone number isn’t valid. ${PHONE_HINT}`);
      return;
    }
    if (collected && (!origin.contactName.trim() || !origin.contactPhone.trim())) {
      setFormError(
        `${carrier} collects the return, so add a contact person and phone number for the driver to call.`,
      );
      return;
    }
    const missing = packageMissingMeasurements(pallets.map(palletMeasurements));
    if (missing !== null) {
      setFormError(
        `Pallet ${missing} needs its weight, length, width and height. Carriers charge extra for missing measurements.`,
      );
      return;
    }
    mutation.mutate({
      carrier,
      originSiteId: siteId,
      origin: {
        name: origin.name.trim(),
        contactName: origin.contactName.trim() || undefined,
        contactPhone: tidyPhone(origin.contactPhone) || undefined,
        address,
      },
      destinationSiteId,
      pallets: packages,
      reason: reason.trim() || undefined,
      senderReference: senderReference.trim(),
    });
  }

  const loadingReferences = customers.isLoading || sites.isLoading;
  const routeFrom = origin.name.trim() || 'Customer site';
  const routeTo = destination?.name ?? 'Tidal location';

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label="Create customer return"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="drawer-heading">
          <h2>New customer return</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close new shipment drawer"
          >
            ×
          </button>
        </div>

        <DirectionChoice
          value="RETURN"
          onChange={onDirectionChange}
          disabled={mutation.isPending}
        />

        <p className="shipment-route" aria-label="Return route">
          <span className="direction-badge direction-return">Return</span>
          <span>
            From <strong>{routeFrom}</strong> → To <strong>{routeTo}</strong>
          </span>
        </p>
        <p className="drawer-version-note">
          Booked in Cargonizer. The label and instructions for the customer are ready on the
          shipment afterwards.
        </p>

        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['shipments'] });
              onClose();
            }}
          />
        ) : null}
        {customers.isError || sites.isError ? (
          <p role="alert" className="form-error">
            Customers or sites could not be loaded. Close this form and try again.
          </p>
        ) : null}

        <fieldset className="drawer-section">
          <legend>From: customer site</legend>
          <label>
            Customer site
            <SearchableSelect
              ariaLabel="Return origin site"
              value={siteId}
              onChange={applySite}
              options={siteOptions}
              placeholder={loadingReferences ? 'Loading sites…' : 'Search by site or customer'}
              disabled={loadingReferences}
            />
          </label>
          {selectedCustomer ? (
            <p className="drawer-version-note">Customer: {customerLabel(selectedCustomer)}</p>
          ) : null}
          {selectedSite && !hasSiteAddress(selectedSite.address) ? (
            <p className="drawer-version-note" role="status">
              This site has no address on file. Enter the pickup address for this return below, or
              add it to the site under Customers → Sites.
            </p>
          ) : null}
          {selectedSite && hasSiteAddress(selectedSite.address) ? (
            <p className="drawer-version-note">
              Prefilled from the site. Changes below apply to this return only.
            </p>
          ) : null}
          <label>
            Pickup name
            <input
              value={origin.name}
              onChange={(event) => updateOrigin('name', event.target.value)}
              aria-describedby={`${formId}-name-hint`}
            />
            <span className="drawer-version-note" id={`${formId}-name-hint`}>
              Printed on the label as the sender.
            </span>
          </label>
          <label>
            {collected ? 'Contact person (required)' : 'Contact person'}
            <input
              value={origin.contactName}
              onChange={(event) => updateOrigin('contactName', event.target.value)}
              required={collected}
            />
          </label>
          <label>
            {collected ? 'Phone number (required)' : 'Phone number (optional)'}
            <input
              type="tel"
              value={origin.contactPhone}
              onChange={(event) => updateOrigin('contactPhone', event.target.value)}
              aria-invalid={!isValidPhone(origin.contactPhone)}
              aria-describedby={`${formId}-phone-hint`}
              required={collected}
            />
            <span className="drawer-version-note" id={`${formId}-phone-hint`}>
              {collected
                ? 'The driver calls this number when arriving to collect the return.'
                : 'The customer drops the return off, so a phone number is optional.'}
              {!isValidPhone(origin.contactPhone) ? ` ${PHONE_HINT}` : ''}
            </span>
          </label>
          <label>
            Street address
            <input
              value={origin.line1}
              onChange={(event) => updateOrigin('line1', event.target.value)}
            />
          </label>
          <label>
            Address line 2 (optional)
            <input
              value={origin.line2}
              onChange={(event) => updateOrigin('line2', event.target.value)}
            />
          </label>
          <div className="form-row">
            <label>
              Postcode
              <input
                value={origin.postcode}
                onChange={(event) => updateOrigin('postcode', event.target.value)}
              />
            </label>
            <label>
              City
              <input
                value={origin.city}
                onChange={(event) => updateOrigin('city', event.target.value)}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="drawer-section">
          <legend>To: Tidal location</legend>
          {destinations.isError ? (
            <div role="alert" className="form-error">
              Tidal return locations could not be loaded.{' '}
              <button type="button" onClick={() => void destinations.refetch()}>
                Retry
              </button>
            </div>
          ) : null}
          {destinations.isSuccess && destinations.data.length === 0 ? (
            <p role="alert" className="form-error">
              No Tidal return locations are configured, so returns can’t be booked yet.
            </p>
          ) : null}
          <label>
            Destination
            <select
              value={destinationSiteId}
              onChange={(event) => setDestinationSiteId(event.target.value)}
              disabled={!destinations.isSuccess || destinations.data.length === 0}
            >
              {destinations.isLoading ? <option value="">Loading…</option> : null}
              {(destinations.data ?? []).map((d) => (
                <option key={d.siteId} value={d.siteId}>
                  {d.name}
                  {d.address
                    ? ` — ${d.address.line1}, ${d.address.postcode} ${d.address.city}`
                    : ''}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <label>
          Return product
          <select
            value={carrier}
            onChange={(event) => setCarrier(event.target.value as ReturnCarrier)}
          >
            {CARGONIZER_RETURN_CARRIERS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label>
          Reason (optional)
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. 2 servere i retur"
            aria-describedby={`${formId}-reason-hint`}
          />
          <span className="drawer-version-note" id={`${formId}-reason-hint`}>
            Printed on the label as the content.
          </span>
        </label>
        <RebillFreightField
          value={senderReference}
          onChange={setSenderReference}
          options={rebillOptions(
            sites.data ?? [],
            sites.data?.find((candidate) => candidate.id === siteId)?.customerId,
            siteId,
          )}
          disabled={mutation.isPending}
        />

        <div className="form-divider">
          <span>{shipsBoxes(carrier) ? 'Boxes' : 'Pallets'}</span>
        </div>
        <PalletEditor
          pallets={pallets}
          onChange={setPallets}
          measurementsRequired
          allowCopies
          boxes={shipsBoxes(carrier)}
        />

        <div className="drawer-actions">
          <button type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            className="primary"
            disabled={mutation.isPending || !canMutate}
            title={canMutate ? undefined : 'Sign in to book returns'}
          >
            {mutation.isPending ? 'Booking…' : 'Book return via Cargonizer'}
          </button>
        </div>
      </form>
    </div>
  );
}
